/**
 * Singleton axios client with cookies + CSRF + 409 reauth handling.
 *
 * - `withCredentials: true` so `sid` and `csrf` cookies travel on every call.
 * - Request interceptor pulls the `csrf` cookie value (set by login on the
 *   backend, mirrored as a non-HttpOnly cookie) and attaches it as
 *   `X-CSRF-Token` for every non-GET request.
 * - Response interceptor catches `409 GIGAFY_REAUTH_REQUIRED` and dispatches
 *   a custom DOM event the auth context listens for; consumers can re-prompt
 *   the user without unmounting the current page.
 */
import axios, { type AxiosError } from "axios";

export const REAUTH_EVENT = "chassis-reauth-required";

export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

function readCookie(name: string): string | null {
  const cookies = document.cookie.split(";").map((c) => c.trim());
  for (const c of cookies) {
    const eq = c.indexOf("=");
    if (eq > -1 && c.slice(0, eq) === name) {
      return decodeURIComponent(c.slice(eq + 1));
    }
  }
  return null;
}

api.interceptors.request.use((config) => {
  const method = (config.method ?? "get").toLowerCase();
  if (method !== "get" && method !== "head" && method !== "options") {
    const csrf = readCookie("csrf");
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: { code?: string; detail?: string } }>) => {
    const code = error.response?.data?.error?.code;
    if (error.response?.status === 409 && code === "GIGAFY_REAUTH_REQUIRED") {
      window.dispatchEvent(new CustomEvent(REAUTH_EVENT));
    }
    // Always log the server-correlated request id (set by the backend's
    // RequestIdMiddleware) so a failure is traceable from devtools even
    // where the UI doesn't surface it in a toast.
    const ref = readRequestId(error);
    if (ref) {
      // eslint-disable-next-line no-console
      console.warn(`API error [ref ${ref}]`, error.config?.url, code ?? error.message);
    }
    return Promise.reject(error);
  },
);

function readRequestId(err: AxiosError): string | undefined {
  const h = err.response?.headers as Record<string, string> | undefined;
  // axios lower-cases response header keys.
  return h?.["x-request-id"] || undefined;
}

export type ApiError = {
  status: number;
  code: string;
  detail: string;
  /** Server request id (X-Request-ID) for support / log correlation. */
  requestId?: string;
};

export function asApiError(err: unknown, fallback = "Unexpected error"): ApiError {
  // Callers often pass a query/mutation ``error`` that is ``null`` until
  // something actually fails — guard so reading ``.response`` can't throw.
  if (!err || typeof err !== "object") {
    return { status: 0, code: "UNKNOWN", detail: fallback };
  }
  const axiosErr = err as AxiosError<{ error?: { code?: string; detail?: string } }>;
  const status = axiosErr.response?.status ?? 0;
  const code = axiosErr.response?.data?.error?.code ?? "UNKNOWN";
  const detail = axiosErr.response?.data?.error?.detail ?? axiosErr.message ?? fallback;
  return { status, code, detail, requestId: readRequestId(axiosErr) };
}

/**
 * Human-facing error string with the server request id appended when
 * present, so an operator can quote it ("include this ref when reporting").
 * Drop-in replacement for ``asApiError(err).detail`` in toasts/banners.
 */
export function apiErrorMessage(err: unknown, fallback = "Unexpected error"): string {
  const e = asApiError(err, fallback);
  const detail = e.detail || fallback;
  return e.requestId ? `${detail} (ref ${e.requestId})` : detail;
}
